import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart } from '@antv/g2';
import { v4 as uuidv4 } from 'uuid';
import { RappiderChartBaseModule } from '../chart-base/chart-base.module';

export interface G2CandlestickChartData {
  timestamp?: string;
  min?: number;
  max?: number;
  average?: number;
}

export interface G2CandlestickChartInput {
  chartTitle: string;
  xAxisTitle: string;
  yAxisTitle: string;
  data: G2CandlestickChartData[];
}

@Component({
  selector: 'rappider-g2-candlestick-chart',
  standalone: true,
  imports: [
    CommonModule,
    RappiderChartBaseModule
  ],
  templateUrl: './app-g2-candlestick-chart.component.html',
  styleUrls: ['./app-g2-candlestick-chart.component.scss'],
})
export class AppG2CandlestickChartComponent implements AfterViewInit, OnChanges {

  @Input() chartTitle?: string = '';
  @Input() xAxisTitle?: string = '';
  @Input() yAxisTitle?: string = '';
  @Input() chartHeight?: number = 400;
  @Input() config?: any | undefined;

  @Input() data?: G2CandlestickChartData[] = [];

  ngAfterViewInit(): void {
    if (this.data?.length > 0) {
      this.renderConfig();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.renderConfig();
    }
  }

  renderConfig() {
    const config = {
      type: 'view',
      autoFit: true,
      data: this.data,
      // The parent 'encode' now only defines what is common to ALL children.
      // Color encoding has been removed from here to prevent conflicts.
      encode: {
        x: 'timestamp',
      },
      scale: {
        x: { compare: (a, b) => new Date(a).getTime() - new Date(b).getTime() },
        title: this.xAxisTitle
        // The parent color scale is also removed. It will be defined on each mark.
      },
      axis: {
        y: { title: this.yAxisTitle },
        x: { title: this.xAxisTitle } // You can also define it here instead of in scale
      },
      children: [
        // --- Child 1: The vertical line for min/max range ---
        {
          type: 'link',
          encode: { y: ['min', 'max'] },
          style: { stroke: 'grey' }, // Give it a neutral color
          tooltip: false,
        },
        // --- Child 2: The candlestick/bar for min/max range ---
        {
          type: 'interval',
          encode: {
            y: ['min', 'max'],
            // MOVED HERE: The trend-based color encoding is now defined directly on the interval mark.
            color: (d, i, data) => {
              if (i === 0) {
                return 'Unchanged';
              }
              const prevDay = data[i - 1];
              const trend = d.average - prevDay.average;
              return trend > 0 ? 'Up' : trend === 0 ? 'Unchanged' : 'Down';
            },
          },
          // MOVED HERE: The color scale for the bars is now defined here too.
          scale: {
            color: {
              domain: ['Up', 'Unchanged', 'Down'],
              range: ['#4daf4a', '#999999', '#e41a1c'],
            },
          },
          tooltip: {
            title: 'timestamp',
            items: [
              { field: 'min', name: 'Min' },
              { field: 'max', name: 'Max' },
            ],
          },
        },
        // --- Child 3: The line chart for the average value ---
        {
          type: 'line',
          encode: {
            y: 'average',
            color: () => 'Average', // This mark has its own independent color encoding
          },
          scale: {
            color: {
              domain: ['Average'],
              // range: ['#1677ff'], // It uses its own scale
            },
          },
          style: {
            lineWidth: 4,
          },
          tooltip: {
            title: 'timestamp',
            items: [{ field: 'average', name: 'Average' }],
          },
        },
        // --- Child 4: Points on the line chart ---
        {
          type: 'point',
          encode: {
            y: 'average',
            color: () => 'Average', // This also uses the 'Average' color scale
          },
          scale: {
            color: {
              domain: ['Average'],
              // range: ['#1677ff'],
            },
          },
          style: {
            // fill: '#1677ff',
            lineWidth: 2,
            stroke: '#ffffff',
          },
          tooltip: {
            title: 'timestamp',
            items: [{ field: 'average', name: 'Average' }],
          },
        },
      ],
    };
    this.config = config;
  }

}
